import { ALL_SHELL_VIEW_MODES, ShellViewMode } from '../interfaces/shell-view-mode';

/**
 * Posizionamento delle azioni del portale (`SiaActionsContainerComponent`)
 * sulla shell desktop.
 *
 * - `'header'` (default): le azioni vivono dentro `sia-headerbar`, accanto al titolo.
 * - `'bottom'`: le azioni vivono in una bottom bar sticky in fondo alla shell,
 *   in modalità desktop standard (label + icone). Su mobile il comportamento
 *   non cambia: il footer mobile esistente continua ad essere usato.
 */
export type RuntimeActionsPosition = 'header' | 'bottom';

/**
 * Appearance di default applicata a tutti i `mat-form-field` del portale.
 * Iniettata globalmente nel bootstrap di ogni installazione tramite il
 * provider {@link provideFormFieldAppearance}, che usa `MAT_FORM_FIELD_DEFAULT_OPTIONS`
 * con factory basata su {@link getFormFieldAppearance}. Permette di scegliere
 * lo stile Material 3 senza toccare i template della libreria `sia-input`.
 *
 * Coprendo l'intera app, l'appearance si applica anche a filter-panel,
 * dialog, top-bar, pagine custom e CRUD. Singoli componenti possono
 * sovrascriverla via `viewProviders` locale (es. `sia-mat-grid` forza `'fill'`).
 *
 * - `'outline'` (default): bordo continuo con label flottante. Match storico.
 * - `'fill'`: superficie riempita stile filled text-field.
 */
export type RuntimeFormFieldAppearance = 'outline' | 'fill';

/**
 * Quando deve essere mostrato il messaggio di errore rosso ("Obbligatorio")
 * sui campi `required` del componente `sia-crud` / `sia-crud-merge`.
 *
 * - `'afterSave'` (default): l'errore appare solo dopo il primo tentativo di
 *   salvataggio (Save / Save&Back). Comportamento "soft" che evita di
 *   accogliere l'utente con un form gia' tutto rosso.
 * - `'immediate'`: l'errore e' visibile fin dal primo render del form,
 *   anche prima che l'utente abbia toccato qualcosa.
 *
 * Il flag NON influenza il blocco lato logica del save: i campi `required`
 * mancanti impediscono comunque il salvataggio, in entrambe le modalita'.
 */
export type RuntimeCrudRequiredErrorMode = 'afterSave' | 'immediate';

export interface RuntimePwaApp {
  id: string;
  name: string;
  manifestUrl: string;
  icon?: string;
  /**
   * Override opzionale per l'icona iOS (`<link rel="apple-touch-icon">`).
   * Se assente, lo swap usa `icon` come fallback.
   */
  appleTouchIcon?: string;
  description?: string;
}

export interface RuntimeConfig {
  theme: { base: string; variant: 'light' | 'dark' };
  viewMode?: ShellViewMode;
  features?: {
    google?: boolean;
    ai?: boolean;
  };
  variables?: { [key: string]: any };
  ui?: {
    topBarIdentifier?: string;
    /**
     * Override del `componentId` usato per caricare la prospettiva
     * `shell-edge-panels`. Se assente, il default e' `'shell'`.
     */
    shellEdgePanelsId?: string;
    /**
     * Posizionamento delle azioni del portale sulla shell desktop. Default `'header'`.
     * Vedi {@link RuntimeActionsPosition}.
     */
    actionsPosition?: RuntimeActionsPosition;
    /**
     * Se `false`, nasconde il blocco titolo/sottotitolo nella `sia-headerbar`.
     * Default `true`. Sostituisce funzionalmente `viewMode: '... noheaderinfo'`,
     * mantenendo backward compatibility (la stringa legacy continua a funzionare
     * quando il flag e' `undefined` o `true`).
     */
    showHeaderInfo?: boolean;
    /**
     * Se `false`, nasconde il bottone "indietro" della headerbar (desktop) e
     * dell'actions-container mobile. Default `true`. Su mobile lo slot non
     * viene rimosso ma reso `invisible` per non collassare il layout.
     */
    showBackAction?: boolean;
    /**
     * Appearance di default per tutti i `mat-form-field` del portale (vedi
     * {@link RuntimeFormFieldAppearance}). Default `'outline'` quando assente.
     * Letto dall'helper `getFormFieldAppearance()` e iniettato globalmente
     * via `provideFormFieldAppearance()` nei `providers` di `bootstrapApplication`
     * di ogni installazione: copre quindi tutti i form-field del portale
     * (filter-panel, dialog, top-bar, pagine custom, CRUD, ...).
     */
    formFieldAppearance?: RuntimeFormFieldAppearance;
    /**
     * Densita di default per i campi dei componenti `sia-crud` e
     * `sia-crud-merge`. Intero in `[0, 5]` (0 = default Material / meno
     * compatto, 5 = piu compatto). Fa parte della cascade:
     * `CrudField.density` → `CrudConfiguration.density` →
     * `runtime-config.ui.crudFieldDensity` → nessuna classe applicata.
     * Letto via {@link getCrudFieldDensity}.
     */
    crudFieldDensity?: number;
    /**
     * Quando mostrare il messaggio di errore "Obbligatorio" sui campi
     * `required` del CRUD. Default `'afterSave'`. Vedi
     * {@link RuntimeCrudRequiredErrorMode}. Letto via
     * {@link getCrudRequiredErrorMode}.
     */
    crudRequiredErrorMode?: RuntimeCrudRequiredErrorMode;
    /**
     * Se `true` (default), su mobile in orientamento landscape la shell mostra
     * un overlay che invita l'utente a ruotare il dispositivo in verticale.
     * Impostare `false` per disabilitare l'overlay e permettere l'uso del
     * portale anche in landscape. Letto via {@link getLockMobileLandscape}.
     */
    lockMobileLandscape?: boolean;
  };
  pwaApps?: RuntimePwaApp[];
  /**
   * Override parziali per-app PWA. La chiave deve combaciare con `pwaApps[].id`.
   * Al boot, viene rilevato il manifest attivo da `<link rel="manifest">` e,
   * se trovata una corrispondenza in `pwaApps`, la sezione `apps[id]` viene
   * deep-mergiata sopra la configurazione root (override vince sui default).
   */
  apps?: Record<string, DeepPartialRuntimeConfig>;
}

export type DeepPartialRuntimeConfig = {
  theme?: Partial<RuntimeConfig['theme']>;
  viewMode?: RuntimeConfig['viewMode'];
  features?: RuntimeConfig['features'];
  variables?: RuntimeConfig['variables'];
  ui?: RuntimeConfig['ui'];
  pwaApps?: RuntimePwaApp[];
};

const DEFAULTS: RuntimeConfig = {
  theme: { base: 'default', variant: 'light' },
  viewMode: undefined,
};

let cached: RuntimeConfig = DEFAULTS;

export async function loadRuntimeConfig(url = '/runtime-config.json'): Promise<RuntimeConfig> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      cached = {
        theme: {
          base: typeof data?.theme?.base === 'string' ? data.theme.base : DEFAULTS.theme.base,
          variant: data?.theme?.variant === 'dark' ? 'dark' : 'light',
        },
        viewMode: ALL_SHELL_VIEW_MODES.includes(data?.viewMode) ? (data.viewMode as ShellViewMode) : DEFAULTS.viewMode,
        variables:
          data?.variables && typeof data.variables === 'object' && !Array.isArray(data.variables)
            ? { ...data.variables }
            : undefined,
        features:
          data?.features && typeof data.features === 'object'
            ? {
                google: typeof data.features.google === 'boolean' ? data.features.google : undefined,
                ai: typeof data.features.ai === 'boolean' ? data.features.ai : undefined,
              }
            : undefined,
        ui:
          data?.ui && typeof data.ui === 'object'
            ? {
                topBarIdentifier: typeof data.ui.topBarIdentifier === 'string' ? data.ui.topBarIdentifier : undefined,
                shellEdgePanelsId:
                  typeof data.ui.shellEdgePanelsId === 'string' && data.ui.shellEdgePanelsId.length > 0
                    ? data.ui.shellEdgePanelsId
                    : undefined,
                actionsPosition: data.ui.actionsPosition === 'bottom' ? 'bottom' : undefined,
                showHeaderInfo: typeof data.ui.showHeaderInfo === 'boolean' ? data.ui.showHeaderInfo : undefined,
                showBackAction: typeof data.ui.showBackAction === 'boolean' ? data.ui.showBackAction : undefined,
                formFieldAppearance:
                  data.ui.formFieldAppearance === 'outline' || data.ui.formFieldAppearance === 'fill'
                    ? (data.ui.formFieldAppearance as RuntimeFormFieldAppearance)
                    : undefined,
                crudFieldDensity: (() => {
                  const raw = data.ui.crudFieldDensity;
                  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
                    return undefined;
                  }
                  const n = Math.trunc(raw);
                  const clamped = Math.max(0, Math.min(5, n));
                  return clamped;
                })(),
                crudRequiredErrorMode:
                  data.ui.crudRequiredErrorMode === 'immediate' || data.ui.crudRequiredErrorMode === 'afterSave'
                    ? (data.ui.crudRequiredErrorMode as RuntimeCrudRequiredErrorMode)
                    : undefined,
                lockMobileLandscape:
                  typeof data.ui.lockMobileLandscape === 'boolean' ? data.ui.lockMobileLandscape : undefined,
              }
            : undefined,
        pwaApps: Array.isArray(data?.pwaApps)
          ? (data.pwaApps as any[])
              .filter(
                (a) =>
                  a &&
                  typeof a.id === 'string' &&
                  a.id.length > 0 &&
                  typeof a.manifestUrl === 'string' &&
                  a.manifestUrl.length > 0,
              )
              .map((a) => ({
                id: String(a.id),
                name: typeof a.name === 'string' && a.name.length > 0 ? String(a.name) : String(a.id),
                manifestUrl: String(a.manifestUrl),
                icon: typeof a.icon === 'string' ? String(a.icon) : undefined,
                appleTouchIcon: typeof a.appleTouchIcon === 'string' ? String(a.appleTouchIcon) : undefined,
                description: typeof a.description === 'string' ? String(a.description) : undefined,
              }))
          : undefined,
      };
      if (cached.pwaApps && cached.pwaApps.length === 0) {
        cached.pwaApps = undefined;
      }

      // Parsing della sezione `apps`: override per-app indicizzati per id manifest.
      const appsRaw = data?.apps;
      if (appsRaw && typeof appsRaw === 'object' && !Array.isArray(appsRaw)) {
        const sanitized: Record<string, DeepPartialRuntimeConfig> = {};
        for (const k of Object.keys(appsRaw)) {
          const v = appsRaw[k];
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            sanitized[k] = v as DeepPartialRuntimeConfig;
          }
        }
        cached.apps = Object.keys(sanitized).length > 0 ? sanitized : undefined;
      }

      // Selezione app attiva e deep-merge sopra la root.
      const activeId = detectActiveAppId(cached);
      if (activeId && cached.apps && cached.apps[activeId]) {
        cached = deepMergeRuntimeConfig(cached, cached.apps[activeId]);
      }
    }
  } catch {
    // keep defaults
  }
  return cached;
}

/**
 * Identifica l'app PWA attiva.
 * Strategia (in ordine):
 *  1. Cerca il sentinel `/launch/<id>` nel path della URL: prima in
 *     `window.location.pathname`, poi in `window.location.hash` (il portale usa
 *     hash routing tramite `withHashLocation`, quindi la route reale vive dopo
 *     il `#`). Quando una PWA installata viene riaperta, il browser la lancia
 *     sullo `start_url` del manifest, che e' costruito proprio per portare il
 *     sentinel nel path: a differenza del query param, il path sopravvive al
 *     freeze/resume della webview iOS. Quando trovato, l'id viene anche
 *     persistito in localStorage perche' la route Angular dedicata fa subito
 *     uno strip del segmento `/launch/<id>` per pulire la history.
 *  2. Legge `localStorage['NODELETE-sia.pwa.selectedAppId']`: se e' una stringa
 *     non vuota che combacia con un `pwaApps[].id`, viene usata come app
 *     attiva. Necessario perche' `loadRuntimeConfig` viene invocato in
 *     `main.ts` PRIMA di `bootstrapApplication`, quando `<link rel="manifest">`
 *     punta ancora al manifest hardcoded dell'`index.html` (lo swap viene
 *     fatto solo dopo da `SiaPwaInstallService`).
 *  3. Fallback al match per `<link rel="manifest">` href, robusto se
 *     localStorage e' vuoto o l'utente non ha mai installato/scelto un'app.
 *
 * Ritorna `null` se non c'e' alcuna corrispondenza (lista vuota, mismatch,
 * ambiente non-browser).
 */
function detectActiveAppId(cfg: RuntimeConfig): string | null {
  try {
    if (typeof document === 'undefined') return null;
    if (!cfg.pwaApps || cfg.pwaApps.length === 0) return null;

    // Deve restare allineato a STORAGE_KEY in sia-pwa-install.service.ts.
    // Non importarla per evitare cicli di dipendenza.
    const PWA_SELECTED_APP_ID_KEY = 'NODELETE-sia.pwa.selectedAppId';
    /** Sentinel di path encodato nello `start_url` di ogni manifest PWA: `/launch/<appId>/...`. */
    const PWA_LAUNCH_SENTINEL_RE = /(?:^|\/)launch\/([^\/?#]+)/;

    try {
      if (typeof window !== 'undefined' && window.location) {
        // Il portale usa hash routing (`withHashLocation`), quindi il sentinel
        // puo' comparire sia in `location.pathname` (URL "vere") sia dentro
        // `location.hash` (URL hash-based: dopo il `#` c'e' la route Angular,
        // ed e' la' che lo `start_url` del manifest lo colloca). Controlliamo
        // entrambi i contesti.
        const pathname = window.location.pathname || '';
        const hashRaw = window.location.hash || '';
        // Strip del leading `#` dall'hash per uniformare il match della regex.
        const hashStripped = hashRaw.startsWith('#') ? hashRaw.slice(1) : hashRaw;
        const fromPath = PWA_LAUNCH_SENTINEL_RE.exec(pathname)?.[1] ?? null;
        const fromHash = PWA_LAUNCH_SENTINEL_RE.exec(hashStripped)?.[1] ?? null;
        const fromSentinel = fromPath || fromHash;
        if (typeof fromSentinel === 'string' && fromSentinel.length > 0) {
          const match = cfg.pwaApps.find((a) => a.id === fromSentinel);
          if (match) {
            // Persisti per le navigazioni successive: la route `LaunchRedirectComponent`
            // rimuove il sentinel dalla URL e quindi la prossima `detectActiveAppId`
            // (es. dopo un refresh) non lo troverebbe piu'.
            try {
              if (window.localStorage) {
                window.localStorage.setItem(PWA_SELECTED_APP_ID_KEY, match.id);
              }
            } catch {
              // localStorage non scrivibile: ignora, l'id resta valido per questo boot.
            }
            return match.id;
          }
        }
      }
    } catch {
      // window.location non disponibile: fallback agli step successivi.
    }

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem(PWA_SELECTED_APP_ID_KEY);
        if (typeof stored === 'string' && stored.length > 0) {
          const match = cfg.pwaApps.find((a) => a.id === stored);
          if (match) return match.id;
        }
      }
    } catch {
      // localStorage non disponibile (es. SSR, modalità privata bloccata): fallback al manifest link.
    }

    const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!link) return null;
    // `link.href` viene risolto dal browser in URL assoluto; manifestUrl in config è relativo.
    const hrefAbs = link.href;
    const hrefPath = (() => {
      try {
        return new URL(hrefAbs, document.baseURI).pathname;
      } catch {
        return hrefAbs;
      }
    })();
    for (const app of cfg.pwaApps) {
      try {
        const candidatePath = new URL(app.manifestUrl, document.baseURI).pathname;
        if (candidatePath === hrefPath) return app.id;
      } catch {
        // ignore singolo manifestUrl malformato
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Deep-merge selettivo: gli override vincono sulla base, gli oggetti annidati
 * (theme, features, ui) vengono mergiati per chiave. Array (es. `pwaApps`)
 * vengono sostituiti, non concatenati.
 */
function deepMergeRuntimeConfig(base: RuntimeConfig, override: DeepPartialRuntimeConfig): RuntimeConfig {
  const merged: RuntimeConfig = {
    ...base,
    theme: override.theme ? { ...base.theme, ...(override.theme as Partial<RuntimeConfig['theme']>) } : base.theme,
    viewMode: override.viewMode ?? base.viewMode,
    features:
      override.features || base.features ? { ...(base.features ?? {}), ...(override.features ?? {}) } : undefined,
    variables:
      override.variables || base.variables ? { ...(base.variables ?? {}), ...(override.variables ?? {}) } : undefined,
    ui: override.ui || base.ui ? { ...(base.ui ?? {}), ...(override.ui ?? {}) } : undefined,
    pwaApps: override.pwaApps ?? base.pwaApps,
  };
  return merged;
}

export function getRuntimeConfig(): RuntimeConfig {
  return cached;
}

export function getRuntimeVariable(key: string): any {
  return !cached.variables ? null : cached.variables[key];
}

/**
 * Restituisce la posizione delle azioni del portale sulla shell desktop.
 * Centralizza il default a `'header'` cosi' i consumer non devono duplicarlo.
 */
export function getActionsPosition(): RuntimeActionsPosition {
  return cached.ui?.actionsPosition ?? 'header';
}

/**
 * Restituisce `true` se il blocco titolo/sottotitolo della `sia-headerbar`
 * deve essere visibile. Default `true` quando il flag e' `undefined`.
 *
 * NOTA: questo helper esprime SOLO il flag runtime-config; la logica legacy
 * `viewMode: '... noheaderinfo'` resta valutata in `SiaShellViewModeService`
 * e prevale quando questo flag e' `undefined`/`true`. Quando `false`, vince.
 */
export function getShowHeaderInfo(): boolean {
  return cached.ui?.showHeaderInfo ?? true;
}

/**
 * Restituisce `true` se il bottone "indietro" deve essere visibile nella
 * headerbar desktop e nell'actions-container mobile. Default `true`.
 */
export function getShowBackAction(): boolean {
  return cached.ui?.showBackAction ?? true;
}

/**
 * Restituisce l'appearance di default per tutti i `mat-form-field` del
 * portale. Default `'outline'`. Usato dalla factory del provider globale
 * `provideFormFieldAppearance()` che popola `MAT_FORM_FIELD_DEFAULT_OPTIONS`
 * nel bootstrap di ogni installazione, applicandolo all'intero portale
 * (non piu' solo ai componenti CRUD).
 */
export function getFormFieldAppearance(): RuntimeFormFieldAppearance {
  return cached.ui?.formFieldAppearance ?? 'outline';
}

/**
 * Restituisce la densita di default per i campi dei componenti `sia-crud` e
 * `sia-crud-merge`, intero in `[0, 5]` o `undefined` se non configurata.
 * Fa parte della cascade `CrudField.density` → `CrudConfiguration.density` →
 * `runtime-config.ui.crudFieldDensity` → nessuna classe applicata.
 */
/**
 * Restituisce la modalita' di visualizzazione degli errori "required" nei
 * componenti CRUD. Default `'afterSave'` quando il flag non e' configurato.
 */
export function getCrudRequiredErrorMode(): RuntimeCrudRequiredErrorMode {
  return cached.ui?.crudRequiredErrorMode ?? 'afterSave';
}

export function getCrudFieldDensity(): number | undefined {
  return cached.ui?.crudFieldDensity;
}

/**
 * Restituisce `true` se la shell deve bloccare l'orientamento landscape su
 * mobile mostrando un overlay che chiede all'utente di ruotare il dispositivo
 * in verticale. Default `true` quando il flag e' `undefined`, per preservare
 * il comportamento storico.
 */
export function getLockMobileLandscape(): boolean {
  return cached.ui?.lockMobileLandscape ?? true;
}
